import { useRef, useEffect, useCallback, useState } from "react";
import { PoseLandmarker, FilesetResolver, PoseLandmarkerResult } from "@mediapipe/tasks-vision";
import { PersonTracker, PersonPose, PersonBounds, PersonTrackingState } from "../utils/personTracker";

type VideoRef = React.RefObject<HTMLVideoElement>;

// Must match the installed @mediapipe/tasks-vision version (pinned in package.json).
// Using @latest breaks loading when a new release ships: the JS glue and WASM binary
// must come from the same version or WebAssembly.instantiate throws a LinkError.
const MEDIAPIPE_WASM_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm";

// ── Configuration constants ─────────────────────────────────────────────────
/** Run pose detection every N frames to save CPU */
const POSE_DETECTION_INTERVAL = 5; // Process every 5th frame (~2 FPS at 10 base FPS)
/** Minimum confidence for pose detection */
const MIN_POSE_CONFIDENCE = 0.5;
/** Threshold for determining if person is facing camera (based on landmark visibility) */
const FACING_CAMERA_THRESHOLD = 0.7;

export interface PersonDetectionStatus {
  /** Total number of persons detected in current frame */
  totalPersons: number;
  /** Main candidate (test-taker) */
  mainCandidate: PersonPose | null;
  /** Other persons detected */
  otherPersons: PersonPose[];
  /** All tracked persons with IDs */
  trackedPersons: PersonPose[];
  /** Active suspicion flags */
  flags: string[];
  /** Tracking statistics */
  stats: {
    totalPersonsDetected: number;
    maxSimultaneousPersons: number;
    averagePersonCount: number;
  };
  /** Canvas with pose overlay (for debugging/visualization) */
  poseCanvas: HTMLCanvasElement | null;
  /** Is model ready */
  ready: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Hook for person detection using MediaPipe Pose Landmarker
 * Detects full body silhouettes, tracks persons with IDs, and identifies suspicious behaviors
 */
export function usePersonDetection(
  videoRef: VideoRef,
  enabled: boolean = true
): PersonDetectionStatus {
  const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
  const personTrackerRef = useRef<PersonTracker>(new PersonTracker());
  const poseCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameCountRef = useRef(0);
  const rafIdRef = useRef(0);
  const lastProcessTimeRef = useRef(0);

  const [status, setStatus] = useState<PersonDetectionStatus>({
    totalPersons: 0,
    mainCandidate: null,
    otherPersons: [],
    trackedPersons: [],
    flags: [],
    stats: {
      totalPersonsDetected: 0,
      maxSimultaneousPersons: 0,
      averagePersonCount: 0,
    },
    poseCanvas: null,
    ready: false,
    error: null,
  });

  // ── Initialize MediaPipe Pose Landmarker ────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    let mounted = true;

    (async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          MEDIAPIPE_WASM_URL
        );

        if (!mounted) return;

        const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/latest/pose_landmarker_heavy.task",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numPoses: 5, // Detect up to 5 persons
          minPoseDetectionConfidence: MIN_POSE_CONFIDENCE,
          minPosePresenceConfidence: MIN_POSE_CONFIDENCE,
          minTrackingConfidence: MIN_POSE_CONFIDENCE,
        });

        if (!mounted) return;

        poseLandmarkerRef.current = poseLandmarker;

        // Create canvas for visualization
        const canvas = document.createElement("canvas");
        poseCanvasRef.current = canvas;

        setStatus((prev) => ({
          ...prev,
          ready: true,
          poseCanvas: canvas,
          error: null,
        }));
      } catch (err) {
        console.error("Failed to initialize Pose Landmarker:", err);
        if (mounted) {
          setStatus((prev) => ({
            ...prev,
            ready: false,
            error: err instanceof Error ? err.message : "Failed to load pose detection model",
          }));
        }
      }
    })();

    return () => {
      mounted = false;
      if (poseLandmarkerRef.current) {
        poseLandmarkerRef.current.close();
        poseLandmarkerRef.current = null;
      }
    };
  }, [enabled]);

  // ── Process video frames ─────────────────────────────────────────────────────
  const processFrame = useCallback(() => {
    const video = videoRef.current;
    const poseLandmarker = poseLandmarkerRef.current;
    const canvas = poseCanvasRef.current;

    if (!enabled || !video || !poseLandmarker || !canvas || video.readyState < 2) {
      rafIdRef.current = requestAnimationFrame(processFrame);
      return;
    }

    frameCountRef.current++;

    // Process only every Nth frame
    if (frameCountRef.current % POSE_DETECTION_INTERVAL !== 0) {
      rafIdRef.current = requestAnimationFrame(processFrame);
      return;
    }

    const now = performance.now();
    const timestamp = video.currentTime * 1000; // Convert to ms

    try {
      // Detect poses
      const result = poseLandmarker.detectForVideo(video, now);

      // Update canvas size
      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }

      // Clear canvas
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      // Extract person detections from pose results
      const detections = extractPersonDetections(result, canvas);

      // Update person tracker
      const trackingState = personTrackerRef.current.update(detections, timestamp);

      // Draw pose overlays
      if (ctx && result.landmarks) {
        drawPoseLandmarks(ctx, result, canvas.width, canvas.height);
      }

      // Extract flags from all persons
      const allFlags = new Set<string>();
      for (const person of trackingState.persons) {
        person.flags.forEach((flag) => allFlags.add(flag));
      }

      // Separate main candidate from others
      const mainCandidate = trackingState.persons.find((p) => p.isMainCandidate) || null;
      const otherPersons = trackingState.persons.filter((p) => !p.isMainCandidate);

      setStatus({
        totalPersons: trackingState.persons.length,
        mainCandidate,
        otherPersons,
        trackedPersons: trackingState.persons,
        flags: Array.from(allFlags),
        stats: trackingState.stats,
        poseCanvas: canvas,
        ready: true,
        error: null,
      });

      lastProcessTimeRef.current = now;
    } catch (err) {
      console.error("Error processing pose detection:", err);
    }

    rafIdRef.current = requestAnimationFrame(processFrame);
  }, [enabled, videoRef]);

  // ── Start/stop processing loop ───────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !status.ready) return;

    rafIdRef.current = requestAnimationFrame(processFrame);

    return () => {
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [enabled, status.ready, processFrame]);

  return status;
}

/**
 * Extract person detections from PoseLandmarker results
 */
function extractPersonDetections(
  result: PoseLandmarkerResult,
  canvas: HTMLCanvasElement
): Array<{
  bounds: PersonBounds;
  facingCamera: boolean;
  bodyOrientation: number;
  confidence: number;
}> {
  if (!result.landmarks || result.landmarks.length === 0) {
    return [];
  }

  const detections = [];

  for (let i = 0; i < result.landmarks.length; i++) {
    const landmarks = result.landmarks[i];
    const worldLandmarks = result.worldLandmarks?.[i];

    if (!landmarks || landmarks.length === 0) continue;

    // Calculate bounding box
    let minX = 1,
      minY = 1,
      maxX = 0,
      maxY = 0;

    for (const lm of landmarks) {
      minX = Math.min(minX, lm.x);
      minY = Math.min(minY, lm.y);
      maxX = Math.max(maxX, lm.x);
      maxY = Math.max(maxY, lm.y);
    }

    // Add padding
    const padding = 0.05;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(1, maxX + padding);
    maxY = Math.min(1, maxY + padding);

    const width = maxX - minX;
    const height = maxY - minY;
    const centerX = minX + width / 2;
    const centerY = minY + height / 2;

    const bounds: PersonBounds = {
      x: minX,
      y: minY,
      width,
      height,
      centerX,
      centerY,
    };

    // Determine if facing camera based on landmark visibility
    // Landmarks 11,12 = shoulders, 23,24 = hips, 0 = nose
    const nose = landmarks[0];
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];

    // Check visibility (z-coordinate and visibility score)
    const visibleLandmarks = [nose, leftShoulder, rightShoulder, leftHip, rightHip].filter(
      (lm) => lm && lm.visibility && lm.visibility > FACING_CAMERA_THRESHOLD
    );

    const facingCamera = visibleLandmarks.length >= 4;

    // Estimate body orientation using shoulder and hip positions
    let bodyOrientation = 0;
    if (worldLandmarks && worldLandmarks.length > 24) {
      const leftShoulderWorld = worldLandmarks[11];
      const rightShoulderWorld = worldLandmarks[12];

      if (leftShoulderWorld && rightShoulderWorld) {
        // Calculate angle based on shoulder line
        const shoulderAngle = Math.atan2(
          rightShoulderWorld.y - leftShoulderWorld.y,
          rightShoulderWorld.x - leftShoulderWorld.x
        );
        bodyOrientation = (shoulderAngle * 180) / Math.PI;
      }
    }

    // Calculate average confidence
    const confidence =
      landmarks.reduce((sum, lm) => sum + (lm.visibility || 0), 0) / landmarks.length;

    detections.push({
      bounds,
      facingCamera,
      bodyOrientation,
      confidence,
    });
  }

  return detections;
}

/**
 * Draw pose landmarks on canvas for visualization
 */
function drawPoseLandmarks(
  ctx: CanvasRenderingContext2D,
  result: PoseLandmarkerResult,
  width: number,
  height: number
): void {
  if (!result.landmarks) return;

  // Pose connections (MediaPipe standard)
  const connections = [
    [11, 12], [11, 13], [13, 15], [12, 14], [14, 16], // Arms
    [11, 23], [12, 24], [23, 24], // Torso
    [23, 25], [25, 27], [24, 26], [26, 28], // Legs
    [0, 1], [1, 2], [2, 3], [3, 7], // Face
    [0, 4], [4, 5], [5, 6], [6, 8], // Face
  ];

  for (const landmarks of result.landmarks) {
    // Draw connections
    ctx.strokeStyle = "rgba(0, 255, 0, 0.6)";
    ctx.lineWidth = 2;

    for (const [startIdx, endIdx] of connections) {
      const start = landmarks[startIdx];
      const end = landmarks[endIdx];

      if (start && end && start.visibility && end.visibility &&
          start.visibility > 0.5 && end.visibility > 0.5) {
        ctx.beginPath();
        ctx.moveTo(start.x * width, start.y * height);
        ctx.lineTo(end.x * width, end.y * height);
        ctx.stroke();
      }
    }

    // Draw landmarks
    for (const lm of landmarks) {
      if (lm.visibility && lm.visibility > 0.5) {
        ctx.fillStyle = "rgba(255, 0, 0, 0.8)";
        ctx.beginPath();
        ctx.arc(lm.x * width, lm.y * height, 3, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  }
}
